import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";

import { adminPaths } from "admin/paths";
import { safeHttpUrl } from "admin/contentUtils";
import AdminPageHeader from "components/admin/AdminPageHeader";
import { bookingsRepo } from "data/repositories/bookingsRepo";
import { businessesRepo } from "data/repositories/businessesRepo";
import { usersRepo } from "data/repositories/usersRepo";
import EmptyState from "components/EmptyState";
import { bookingKindLabels, travelProviderLabels } from "data/admin/domain";

const SOURCE_LABEL = { portal: "Portal web", mobile: "Mobil uygulama" };

const STATUS_COLOR = { pending: "warning", confirmed: "success", cancelled: "danger" };
const STATUS_LABEL = { pending: "Bekliyor", confirmed: "Onaylandı", cancelled: "İptal" };

export default function AdminBookingDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [refreshKey, setRefreshKey] = useState(0);

  void refreshKey;
  const booking = bookingsRepo.getById(id);
  const business = booking ? businessesRepo.getById(booking.businessId) : null;
  const user = booking ? usersRepo.getById(booking.customerUserId) : null;

  if (!booking) {
    return (
      <>
        <div className="header bg-gradient-dark pb-6 pt-5 pt-md-7" />
        <Container className="mt--6" fluid>
          <EmptyState title="Rezervasyon bulunamadı" />
        </Container>
      </>
    );
  }

  const updateStatus = (status) => {
    bookingsRepo.updateStatus(booking.id, status);
    setRefreshKey((value) => value + 1);
  };

  const kind = booking.kind || "stay";
  const travel = booking.travel;

  return (
    <>
      <AdminPageHeader
        title="Rezervasyon detayı"
        description="İşletme ve kullanıcı ilişkileri görünür; completed gibi model dışı durumlar sunulmaz."
        backAction={() => navigate(`/admin${adminPaths.admin.bookings}`)}
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="8">
            <Card className="shadow mb-4">
              <CardHeader className="border-0">
                <h3 className="mb-0">Rezervasyon özeti</h3>
              </CardHeader>
              <CardBody>
                <div className="mb-3"><strong>ID:</strong> {booking.id}</div>
                <div className="mb-3"><strong>Tür:</strong> {bookingKindLabels[kind]}</div>
                {travel ? (
                  <>
                    <div className="mb-3"><strong>Başlık:</strong> {travel.title}</div>
                    {travel.subtitle ? <div className="mb-3"><strong>Açıklama:</strong> {travel.subtitle}</div> : null}
                    <div className="mb-3"><strong>Sağlayıcı:</strong> {travelProviderLabels[travel.provider] || travel.provider || "-"}</div>
                    <div className="mb-3"><strong>Başlangıç:</strong> {booking.startDate || "-"}</div>
                    {travel.endDate ? <div className="mb-3"><strong>Bitiş:</strong> {travel.endDate}</div> : null}
                    {travel.travelers ? <div className="mb-3"><strong>Kişi:</strong> {travel.travelers}</div> : null}
                    {travel.priceText ? <div className="mb-3"><strong>Fiyat:</strong> {travel.priceText}</div> : null}
                    <div className="mb-3"><strong>Kaynak:</strong> {SOURCE_LABEL[travel.source] || travel.source || "-"}</div>
                    {safeHttpUrl(travel.partnerUrl) ? (
                      <div className="mb-3">
                        <strong>Sağlayıcı bağlantısı:</strong>{" "}
                        <a href={safeHttpUrl(travel.partnerUrl)} target="_blank" rel="noopener noreferrer">Sağlayıcıda aç</a>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="mb-3"><strong>Başlangıç:</strong> {booking.startDate || "-"}</div>
                    <div className="mb-3"><strong>Gece:</strong> {booking.nights || 1}</div>
                  </>
                )}
                <div className="mb-3"><strong>Notlar:</strong> {booking.notes || "-"}</div>
                <div className="mb-3">
                  <strong>Durum:</strong>{" "}
                  <Badge color={STATUS_COLOR[booking.status] || "secondary"} pill>
                    {STATUS_LABEL[booking.status] || booking.status}
                  </Badge>
                </div>
                {booking.status === "pending" ? (
                  <>
                    <Button color="success" size="sm" className="mr-2" onClick={() => updateStatus("confirmed")}>Onayla</Button>
                    <Button color="danger" size="sm" onClick={() => updateStatus("cancelled")}>İptal Et</Button>
                  </>
                ) : null}
                {booking.status === "confirmed" ? <Button color="danger" size="sm" onClick={() => updateStatus("cancelled")}>İptal Et</Button> : null}
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="shadow mb-4">
              <CardHeader className="border-0">
                <h3 className="mb-0">İlişkiler</h3>
              </CardHeader>
              <CardBody>
                {travel ? null : (
                  <div className="mb-3">
                    <strong>İşletme:</strong>
                    <div>{business?.name || booking.businessId}</div>
                    <div className="small text-muted">{booking.businessId}</div>
                    {business ? <Link to={`/admin/businesses/${business.id}`}>İşletme detayına git</Link> : null}
                  </div>
                )}
                <div>
                  <strong>Kullanıcı:</strong>
                  <div>{user?.displayName || booking.customerDisplayName || booking.customerName || "-"}</div>
                  <div className="small text-muted">{user?.email || booking.customerUserId || "-"}</div>
                  {booking.customerUserId ? <Link to={`/admin${adminPaths.admin.userDetail(booking.customerUserId)}`}>Kullanıcı detayına git</Link> : null}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
